import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { AxiosError, AxiosResponse } from "axios";
import { observer } from "mobx-react";
import { getRequest, patchRequest, postRequest } from "../../../../utils/httpRequest";
import { getStripe } from "../../../../utils/stripe";
import { notification } from "../../../../utils/helpers";
import CardEditForm from "./CardEditForm";
import { CoursePaymentPlan, Subscription } from "../../../../stores/ProfileStore/Types";

interface Card {
  id: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  last4: string;
}

const PAYMENT_PLAN_INSTALLMENTS = 3;

const SubscriptionInfo = ({ profileResource, profileStore }) => {
  const [subscription, setSubscription] = useState<Subscription | undefined>(
    profileResource?.subscription,
  );
  const [coursePaymentPlans, setCoursePaymentPlans] = useState<
    CoursePaymentPlan[]
  >(profileResource?.course_payment_plans || []);
  const [cards, setCards] = useState<Card[]>([]);
  const [showCardForm, setShowCardForm] = useState(false);
  const [savingRenewal, setSavingRenewal] = useState(false);
  // The plan whose "continue your payment plan?" question is open, the one
  // whose payment is being taken, the one just unlocked, and why the last
  // attempt failed.
  const [askingPlanId, setAskingPlanId] = useState<number | null>(null);
  const [resumingPlanId, setResumingPlanId] = useState<number | null>(null);
  const [resumedPlan, setResumedPlan] = useState<CoursePaymentPlan | null>(null);
  const [resumeError, setResumeError] = useState("");

  const renewalText = subscription?.renew ? "renews" : "ends";
  const hasIncompleteCoursePaymentPlans =
    coursePaymentPlans.length > 0 || resumedPlan !== null;
  const isAllAccessSubscription =
    subscription?.active &&
    !subscription.is_community &&
    !subscription.course_payment_plan;
  const cardOnFile = cards[0];
  const shouldLoadPaymentMethods =
    isAllAccessSubscription || hasIncompleteCoursePaymentPlans;

  useEffect(() => {
    setSubscription(profileResource?.subscription);
    setCoursePaymentPlans(profileResource?.course_payment_plans || []);
  }, [
    profileResource?.subscription?.id,
    profileResource?.subscription?.renew,
    profileResource?.subscription?.valid_till,
    profileResource?.course_payment_plans,
  ]);

  useEffect(() => {
    if (!shouldLoadPaymentMethods) return;

    getRequest("/api/payment_methods.json")
      .then((response: AxiosResponse<Card[]>) => {
        setCards(response.data);
      })
      .catch(() => {
        setCards([]);
      });
  }, [shouldLoadPaymentMethods]);

  const toggleRenewal = () => {
    if (!subscription || savingRenewal) return;

    setSavingRenewal(true);

    patchRequest(`/api/subscriptions/${subscription.id}/toggle_status.json`, {})
      .then((response: AxiosResponse<any>) => {
        profileStore.setSubscription(response.data);
        setSavingRenewal(false);
      })
      .catch(() => {
        setSavingRenewal(false);
      });
  };

  const renderIcon = (className) => (
    <span className={`set-sub-ic sub ${className}`}>
      <svg aria-hidden="true">
        <use href="#ln-crown" />
      </svg>
    </span>
  );

  const renderSubscriptionAmount = () => {
    if (!subscription) return null;

    if (subscription.amount_cents > 0) {
      return (
        <>
          {subscription.formatted_amount}/{subscription.interval} ·{" "}
          {renewalText} {subscription.valid_till}.
        </>
      );
    }

    return <>Free subscription · Ends {subscription.valid_till}.</>;
  };

  const renderRenewalControl = () => {
    if (
      !subscription?.active ||
      profileResource?.is_lifetime_subscription ||
      subscription.course_payment_plan
    ) {
      return null;
    }

    const isRenewing = !!subscription.renew;
    const intervalName =
      subscription.interval === "month"
        ? "monthly"
        : subscription.interval === "year"
          ? "yearly"
          : `${subscription.interval}ly`;
    const renewalSubText = isRenewing
      ? `On · renews ${intervalName} on ${subscription.valid_till}.`
      : "Off · access continues until your renewal date.";

    return (
      <>
        <div className="renew-row">
          <div className="renew-copy">
            <div className="renew-title">Auto-renew</div>
            <div className="renew-sub">{renewalSubText}</div>
          </div>
          <div
            className={`ui toggle checkbox set-toggle-checkbox ${
              savingRenewal ? "disabled" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={isRenewing}
              aria-label="Auto-renew subscription"
              disabled={savingRenewal}
              onChange={toggleRenewal}
            />
            <label />
          </div>
        </div>
        {!isRenewing && (
          <div className="renew-note">
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <circle
                cx="8"
                cy="8"
                r="6.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M8 5 V8.5 M8 11 h.01"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            <span>
              Auto-renew is off. You keep access until{" "}
              <strong>{subscription.valid_till}</strong>, then it ends. No
              further payments.
            </span>
          </div>
        )}
      </>
    );
  };

  // A missed instalment stays missed until someone asks for it to be taken
  // again: saving a card only puts the card on file. So the moment a card is
  // saved, the question is asked; the plan's own button asks it too, for
  // anyone who saved their card earlier and wondered why nothing happened.
  const resumablePlan = coursePaymentPlans.find((plan) => plan.resumable);

  const handleCardSaved = (card: Card) => {
    setCards((currentCards) => [card, ...currentCards]);
    setShowCardForm(false);
    if (resumablePlan) {
      setResumeError("");
      setAskingPlanId(resumablePlan.id);
    }
  };

  const resumePlan = (paymentPlan: CoursePaymentPlan) => {
    if (resumingPlanId) return;

    setResumingPlanId(paymentPlan.id);
    setResumeError("");
    requestResume(paymentPlan, {});
  };

  // Some banks want to check a payment with the cardholder (a code, or a tap
  // in their app). Stripe.js shows that check here, then the payment is
  // finished with a second request carrying the confirmed intent.
  const answerBankCheck = async (
    paymentPlan: CoursePaymentPlan,
    clientSecret: string,
  ) => {
    try {
      const stripe = await getStripe();
      if (!stripe) {
        setResumeError("The payment page could not load. Please refresh and try again.");
        setResumingPlanId(null);
        return;
      }

      const { error, paymentIntent } = await stripe.handleCardAction(clientSecret);
      if (error || !paymentIntent) {
        setResumeError(error?.message || "Your bank did not approve the payment.");
        setResumingPlanId(null);
        return;
      }

      requestResume(paymentPlan, { payment_intent_id: paymentIntent.id });
    } catch (unexpected) {
      setResumeError("Your bank's check could not be shown. Please refresh and try again.");
      setResumingPlanId(null);
    }
  };

  const requestResume = (
    paymentPlan: CoursePaymentPlan,
    payload: { payment_intent_id?: string },
  ) => {
    postRequest(`/api/course_payment_plans/${paymentPlan.id}/resume.json`, payload)
      .then(
        (
          response: AxiosResponse<
            CoursePaymentPlan & {
              requires_action?: boolean;
              payment_intent_client_secret?: string;
            }
          >,
        ) => {
        if (response.data.requires_action && response.data.payment_intent_client_secret) {
          answerBankCheck(paymentPlan, response.data.payment_intent_client_secret);
          return;
        }

        const updated = response.data;
        const paidUp =
          (updated.course_payment_plan_payments_made || 0) >=
          PAYMENT_PLAN_INSTALLMENTS;

        setCoursePaymentPlans((plans) =>
          paidUp
            ? plans.filter((plan) => plan.id !== updated.id)
            : plans.map((plan) => (plan.id === updated.id ? updated : plan)),
        );
        setResumedPlan(updated);
        setAskingPlanId(null);
        setResumingPlanId(null);
        notification("Payment taken. Your course is unlocked.", "notification");
      },
      (requestError: AxiosError<{ error: string }>) => {
        setResumeError(
          requestError?.response?.data?.error ||
            "The payment could not be taken. Please check the card and try again.",
        );
        setResumingPlanId(null);
      });
  };

  const renderResumePrompt = (paymentPlan: CoursePaymentPlan) => {
    const busy = resumingPlanId === paymentPlan.id;
    const paymentsAfterThis =
      PAYMENT_PLAN_INSTALLMENTS -
      (paymentPlan.course_payment_plan_payments_made || 0) -
      1;

    return (
      <div className="plan-resume" role="status" id={`planResume-${paymentPlan.id}`}>
        <p>
          Continue your payment plan and start learning now? We take today's
          payment of <strong>{paymentPlan.formatted_amount}</strong> from the
          card on file, and the course unlocks straight away.
          {paymentsAfterThis > 0
            ? " The next payment follows a month from today."
            : " That's the last one; then it's all yours."}
        </p>
        {resumeError && <div className="set-error">{resumeError}</div>}
        <div className="card-edit-actions">
          <button
            type="button"
            className={`btn btn-primary btn--sm ${busy ? "loading" : ""}`}
            disabled={busy}
            onClick={() => resumePlan(paymentPlan)}
          >
            Yes, take the payment
          </button>
          <button
            type="button"
            className="btn btn-ghost btn--sm"
            disabled={busy}
            onClick={() => setAskingPlanId(null)}
          >
            Not now
          </button>
        </div>
      </div>
    );
  };

  const renderResumed = (paymentPlan: CoursePaymentPlan) => (
    <div className="plan-resume is-done" role="status">
      <p>
        Payment taken. <strong>{paymentPlan.course_name}</strong> is unlocked
        again.
      </p>
      {paymentPlan.course_slug && (
        <Link to={`/courses/${paymentPlan.course_slug}`} className="set-link">
          Back to the course <span className="arr">→</span>
        </Link>
      )}
    </div>
  );

  const renderCardEdit = (buttonId: string) => (
    <>
      <button
        type="button"
        className="set-link card-edit-toggle"
        id={buttonId}
        aria-expanded={showCardForm}
        aria-controls="cardEdit"
        onClick={() => setShowCardForm(!showCardForm)}
      >
        <svg aria-hidden="true">
          <use href="#icon-card"></use>
        </svg>
        Edit card details <span className="arr">→</span>
      </button>
      {showCardForm && (
        <Elements stripe={getStripe()}>
          <CardEditForm
            card={cardOnFile}
            onCancel={() => setShowCardForm(false)}
            onSaved={handleCardSaved}
          />
        </Elements>
      )}
    </>
  );

  const renderCoursePaymentPlan = (paymentPlan: CoursePaymentPlan) => {
    const courseName = paymentPlan.course_name || "Course payment plan";
    const paymentsMade = Math.min(
      PAYMENT_PLAN_INSTALLMENTS,
      Math.max(0, paymentPlan.course_payment_plan_payments_made || 0),
    );
    const paymentsLeft = Math.max(0, PAYMENT_PLAN_INSTALLMENTS - paymentsMade);
    const isFinalPayment = paymentsLeft === 1;
    const needsResume = !!paymentPlan.resumable;

    return (
      <div className="set-sub" id={`setSubPlan-${paymentPlan.id}`} key={paymentPlan.id}>
        <div className="set-sub-head">
          <span className="set-sub-ic sub is-plan">
            <svg aria-hidden="true">
              <use href="#icon-card" />
            </svg>
          </span>

          <div>
            <div className="st">{courseName}</div>
            <div className="ss">
              Payment plan · {paymentsMade} of {PAYMENT_PLAN_INSTALLMENTS} paid
            </div>
          </div>
        </div>
        <div
          className="plan-segs"
          role="img"
          aria-label={`${paymentsMade} of ${PAYMENT_PLAN_INSTALLMENTS} payments made`}
        >
          {Array.from({ length: PAYMENT_PLAN_INSTALLMENTS }).map((_, index) => (
            <span
              key={index}
              className={`seg ${
                index < paymentsMade
                  ? "done"
                  : needsResume && index === paymentsMade
                    ? "due"
                    : ""
              }`}
            />
          ))}
        </div>
        {needsResume ? (
          <>
            <p className="plan-next">
              Payment {paymentsMade + 1} of {PAYMENT_PLAN_INSTALLMENTS} didn't go
              through, so the course is locked for now. Add a new card below,
              then continue your plan to unlock it.
            </p>
            {askingPlanId === paymentPlan.id ? (
              renderResumePrompt(paymentPlan)
            ) : (
              <button
                type="button"
                className="set-link plan-resume-toggle"
                id={`planResumeBtn-${paymentPlan.id}`}
                onClick={() => {
                  setResumeError("");
                  setAskingPlanId(paymentPlan.id);
                }}
              >
                <svg aria-hidden="true">
                  <use href="#icon-card" />
                </svg>
                Continue payment plan <span className="arr">→</span>
              </button>
            )}
          </>
        ) : resumedPlan?.id === paymentPlan.id ? (
          renderResumed(paymentPlan)
        ) : (
          <p className="plan-next">
            {isFinalPayment ? "Final" : "Next"} payment of{" "}
            <strong>{paymentPlan.formatted_amount}</strong> on{" "}
            {paymentPlan.valid_till}.
            {isFinalPayment ? " Then it's all yours." : ""}
          </p>
        )}
      </div>
    );
  };

  const renderCoursePaymentPlans = () => {
    if (!hasIncompleteCoursePaymentPlans) return null;

    return (
      <>
        {coursePaymentPlans.map((paymentPlan) =>
          renderCoursePaymentPlan(paymentPlan),
        )}
        {resumedPlan &&
          !coursePaymentPlans.some((plan) => plan.id === resumedPlan.id) &&
          renderResumed(resumedPlan)}
        {renderCardEdit("planCardBtn")}
        <Link to="/passes/year/pay" className="set-upgrade">
          Upgrade to All Access <span className="arr">→</span>
        </Link>
      </>
    );
  };

  const renderSubscription = () => {
    if (profileResource?.is_lifetime_subscription) {
      return (
        <div className="set-sub">
          <div className="set-sub-head">
            {renderIcon("is-lifetime")}

            <div>
              <div className="st">Lifetime Access</div>
              <div className="ss">All courses, forever. No renewal needed.</div>
            </div>
          </div>
        </div>
      );
    }

    if (
      subscription?.active &&
      !subscription.is_community &&
      !subscription.course_payment_plan
    ) {
      return (
        <>
          <div className="set-sub">
            <div className="set-sub-head">
              {renderIcon("is-allaccess")}

              <div>
                <div className="st">All Access Pass</div>
                <div className="ss">
                  {renderSubscriptionAmount()} Every course, all yours.
                </div>
              </div>
            </div>
            {renderRenewalControl()}
            {renderCardEdit("editCardBtn")}
          </div>
          {renderCoursePaymentPlans()}
        </>
      );
    }

    if (subscription?.active && subscription.is_community) {
      return (
        <>
          <div className="set-sub" id="setSubCommunity">
            <div className="set-sub-head">
              {renderIcon("is-community")}

              <div>
                <div className="st">Community member</div>
                <div className="ss">
                  {subscription.formatted_amount}/month · {renewalText}{" "}
                  {subscription.valid_till}. Full community access, plus a course
                  of your choice.
                </div>
              </div>
            </div>
            {renderRenewalControl()}

            <Link to="/passes/month/pay" className="set-upgrade">
              Upgrade to All Access <span className="arr">→</span>
            </Link>
          </div>
          {renderCoursePaymentPlans()}
        </>
      );
    }

    if (hasIncompleteCoursePaymentPlans) {
      return renderCoursePaymentPlans();
    }

    return (
      <div className="set-sub none" id="setSubNone">
        <div className="set-sub-head">
          {renderIcon("is-none")}

          <div>
            <div className="st">No subscription</div>
            <div className="ss">
              You're on the free plan. Join the community, or get full access to
              every course.
            </div>
          </div>
        </div>

        <Link to="/passes/month/pay" className="set-upgrade">
          See all plans <span className="arr">→</span>
        </Link>
      </div>
    );
  };

  return (
    <section className="set-block">
      <div className="set-block-title">
        <svg aria-hidden="true">
          <use href="#ln-crown" />
        </svg>
        Plan
      </div>

      {renderSubscription()}
    </section>
  );
};

export default observer(SubscriptionInfo);
